import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import type { Terrain } from '../game/Terrain';
import { readTune } from '../ui/TuneStore';
import type { RockPlacement } from '../ui/WorldStore';

import { assetUrl as _assetUrl } from '../ui/AssetMap';
const MODEL_SLOT = 'rocks-stylised';
const MODEL_URL_DEFAULT = '/assets/lowpoly-rocks.glb';

interface RocksOptions {
  /** How many rock instances to scatter (used when `placements` is absent). */
  count: number;
  seed?: number;
  /** Pre-computed placements from the shared WorldStore — all clients use these
   *  so the scene layout is identical across machines. */
  placements?: RockPlacement[];
}

/**
 * Replaces the procedural Rocks with scattered instances of the authored
 * Stylised Rocks asset pack. Each rock picks a random variant from the GLB's
 * mesh list; uniform world-space scale is applied per instance.
 */
export class RocksGLB {
  readonly group = new THREE.Group();
  obstacles: Array<{ x: number; z: number; radius: number }> = [];
  /** Placements actually used (generated if the world didn't have any). */
  generatedPlacements: RockPlacement[] = [];

  constructor(private terrain: Terrain, private opts: RocksOptions) {
    this.group.name = 'rocks-glb';
  }

  async load(): Promise<void> {
    const gltf = await new GLTFLoader().loadAsync(_assetUrl(MODEL_SLOT, MODEL_URL_DEFAULT));
    gltf.scene.updateMatrixWorld(true);

    // Collect rock variant meshes. Filter out empty / placeholder nodes.
    const variants: Array<{ geometry: THREE.BufferGeometry; material: THREE.Material; sourceBox: THREE.Box3 }> = [];
    // We mutate each variant's geometry below; no TS enforcement for that.
    gltf.scene.traverse((o) => {
      const m = o as THREE.Mesh;
      if (!m.isMesh || !m.geometry) return;
      const geo = m.geometry.clone();
      geo.applyMatrix4(m.matrixWorld);
      geo.computeBoundingBox();
      const bb = geo.boundingBox!;
      const sz = new THREE.Vector3();
      bb.getSize(sz);
      // Drop degenerate / huge outliers.
      if (sz.length() < 1e-4 || sz.length() > 40) return;
      variants.push({
        geometry: geo,
        material: Array.isArray(m.material) ? m.material[0] : m.material,
        sourceBox: bb,
      });
    });
    if (variants.length === 0) return;

    const tune = readTune('rocks-stylised', { scale: 0.5, rotX: 0, rotY: 0, rotZ: 0, offY: -0.12 });

    // Pre-recenter every variant's geometry so its bbox is XZ-centered at x=z=0
    // and its minimum Y sits at 0. With that, each instance group only needs
    // to be translated to (p.x, terrain + offY, p.z) and the feet land exactly
    // on the ground regardless of whether the GLB's origin was at the centre
    // or a corner of the rock.
    for (const v of variants) {
      const bb = v.sourceBox;
      const cx = (bb.min.x + bb.max.x) * 0.5;
      const cz = (bb.min.z + bb.max.z) * 0.5;
      v.geometry.translate(-cx, -bb.min.y, -cz);
      v.geometry.computeBoundingBox();
      v.sourceBox = v.geometry.boundingBox!.clone();
    }

    const placements: RockPlacement[] = this.opts.placements ?? this.generatePlacements(variants.length);
    this.generatedPlacements = placements;

    for (const p of placements) {
      const variant = variants[Math.min(p.variantIdx, variants.length - 1)];
      const mesh = new THREE.Mesh(variant.geometry, variant.material);
      mesh.castShadow = true;
      mesh.receiveShadow = true;
      const scale = p.scale * tune.scale;
      mesh.position.set(p.x, this.terrain.getHeight(p.x, p.z) + tune.offY, p.z);
      mesh.rotation.set(tune.rotX, p.rotY + tune.rotY, tune.rotZ);
      mesh.scale.setScalar(scale);
      this.group.add(mesh);

      const bbSize = new THREE.Vector3();
      variant.sourceBox.getSize(bbSize);
      const radius = Math.max(bbSize.x, bbSize.z) * scale * 0.45;
      this.obstacles.push({ x: p.x, z: p.z, radius });
    }
  }

  private generatePlacements(variantCount: number): RockPlacement[] {
    const rng = mulberry32(this.opts.seed ?? 42);
    const worldSize = 36;
    const out: RockPlacement[] = [];
    const normalScratch = new THREE.Vector3();
    let attempts = 0;
    while (out.length < this.opts.count && attempts < this.opts.count * 10) {
      attempts++;
      const x = (rng() * 2 - 1) * worldSize;
      const z = (rng() * 2 - 1) * worldSize;
      if (Math.hypot(x + 22, z) < 3 || Math.hypot(x - 22, z) < 3) continue;
      this.terrain.getNormal(x, z, normalScratch);
      if (normalScratch.y < 0.72) continue;
      out.push({
        x, z,
        scale: 0.35 + rng() * 1.1,
        rotY: rng() * Math.PI * 2,
        variantIdx: Math.floor(rng() * variantCount),
      });
    }
    return out;
  }
}

function mulberry32(seed: number) {
  let a = seed >>> 0;
  return function () {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}
